import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery } from "@apollo/client";
import { GET_ROUTING_FORM, PROCESS_ROUTING_FORM } from "./graphql/operations";
import {
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  IconButton,
  Link,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Check as CheckIcon,
  Error as ErrorIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";

// Styled components
const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 800,
  margin: "0 auto",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  boxShadow: theme.shadows[2],
}));

const FormHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  textAlign: "center",
}));

const FieldContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  fontWeight: 500,
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const ThankYouCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 600,
  margin: "0 auto",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  textAlign: "center",
  boxShadow: theme.shadows[2],
}));

// Helper function to get default value based on field type
const getDefaultValue = (fieldType) => {
  switch (fieldType) {
    case "text":
    case "email":
    case "phone":
    case "url":
    case "textarea":
      return "";
    case "select":
      return "";
    case "multi_select":
      return [];
    case "checkbox":
      return false;
    case "number":
      return null;
    case "date":
      return null;
    default:
      return "";
  }
};

// RoutingFormSubmit component
function RoutingFormSubmit() {
  const { formId } = useParams();
  const navigate = useNavigate();

  // State variables
  const [formData, setFormData] = useState({});
  const [fields, setFields] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // GraphQL query to get form data
  const { loading, error, data } = useQuery(GET_ROUTING_FORM, {
    variables: { id: formId },
    skip: !formId,
  });

  // GraphQL query to process form submission
  const [processRoutingForm, { loading: processingForm }] = useLazyQuery(
    PROCESS_ROUTING_FORM,
    {
      onCompleted: (data) => {
        handleRoutingResult(data.processRoutingForm);
      },
      onError: (error) => {
        setNotification({
          open: true,
          message: `Error: ${error.message}`,
          severity: "error",
        });
        setIsSubmitting(false);
      },
    }
  );

  // Initialize form from fetched data
  useEffect(() => {
    if (data && data.getRoutingForm) {
      const form = data.getRoutingForm;
      setFormData(form);

      // Extract fields from form data
      const formFields = form.fields || [];
      setFields(formFields);

      // Initialize form values with defaults based on field types
      const initialValues = {};
      formFields.forEach((field) => {
        initialValues[field.identifier] = getDefaultValue(field.type);
      });

      setFormValues(initialValues);
    }
  }, [data]);

  // Handle form field changes
  const handleFieldChange = (fieldId, value) => {
    setFormValues({
      ...formValues,
      [fieldId]: value,
    });

    // Clear error for this field if it exists
    if (errors[fieldId]) {
      setErrors({
        ...errors,
        [fieldId]: null,
      });
    }
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    fields.forEach((field) => {
      const value = formValues[field.identifier];

      // Check required fields
      if (
        field.required &&
        (value === "" ||
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0))
      ) {
        newErrors[field.identifier] = "This field is required";
        isValid = false;
      }

      // Email validation
      if (
        (field.type === "email" || field.type === "EMAIL") &&
        value &&
        !/\S+@\S+\.\S+/.test(value)
      ) {
        newErrors[field.identifier] = "Please enter a valid email address";
        isValid = false;
      }

      // Phone validation
      if (
        (field.type === "phone" || field.type === "PHONE") &&
        value &&
        !/^\+?[0-9\s\-()]{7,20}$/.test(value)
      ) {
        newErrors[field.identifier] = "Please enter a valid phone number";
        isValid = false;
      }

      // URL validation
      if (
        (field.type === "url" || field.type === "URL") &&
        value &&
        !/^(http|https):\/\/[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}(:[0-9]{1,5})?(\/.*)?$/i.test(
          value
        )
      ) {
        newErrors[field.identifier] = "Please enter a valid URL";
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setNotification({
        open: true,
        message: "Please correct the errors in the form",
        severity: "error",
      });
      return;
    }

    // Start submission process
    setIsSubmitting(true);

    try {
      // Process the routing form
      processRoutingForm({
        variables: {
          formId: formId,
          answers: formValues,
        },
      });
    } catch (error) {
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: "error",
      });
      setIsSubmitting(false);
    }
  };

  // Handle the routing result
  const handleRoutingResult = (result) => {
    setIsSubmitting(false);
    setSubmissionResult(result);

    // Process different destination types
    const { destination, answerId } = result;

    switch (destination.type) {
      case "EXTERNAL_REDIRECT":
        // Prepare to redirect to external URL
        setTimeout(() => {
          // Add answerId as a query parameter to track the submission
          const separator = destination.value.includes("?") ? "&" : "?";
          const redirectUrl = `${destination.value}${separator}form_submission=${answerId}`;
          window.location.href = redirectUrl;
        }, 1500);
        break;

      case "EVENT_REDIRECT":
        // Prepare to redirect to event booking page
        setTimeout(() => {
          // Prepare form data to pass to booking page
          const formDataParam = encodeURIComponent(
            JSON.stringify({
              formSubmissionId: answerId,
              redirectSource: "routing_form",
            })
          );

          // Construct the redirect URL with form submission tracking
          let redirectPath;
          if (destination.value === "custom" && destination.customEventValue) {
            // Handle custom event URL with variable substitution
            let customPath = destination.customEventValue;

            // Replace any variables in the URL with form values
            // Format: {fieldId} will be replaced with the corresponding form value
            Object.entries(formValues).forEach(([key, value]) => {
              const placeholder = `{${key}}`;
              if (customPath.includes(placeholder)) {
                customPath = customPath.replace(
                  new RegExp(placeholder, "g"),
                  encodeURIComponent(value)
                );
              }
            });

            redirectPath = `/calendar/${customPath}?formData=${formDataParam}`;
          } else {
            // Handle standard event type redirection
            redirectPath = `/calendar/event/${destination.value}?formData=${formDataParam}`;
          }

          navigate(redirectPath);
        }, 1500);
        break;

      case "CUSTOM_PAGE":
        // Show custom thank you message - no redirect needed
        // We'll handle this in the render method
        break;

      default:
        console.warn("Unknown destination type:", destination.type);
    }
  };

  // Handle notification close
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // Show loading state while fetching form
  if (loading) {
    return (
      <Container>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Show error if form can't be loaded
  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          Error loading form: {error.message}
        </Alert>
      </Container>
    );
  }

  // Show error if form doesn't exist
  if (!formData._id) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          Form not found or has been removed.
        </Alert>
      </Container>
    );
  }

  // If form submission is complete, show the appropriate result
  if (submissionResult) {
    const { destination } = submissionResult;

    return (
      <Container>
        <ThankYouCard>
          <Box sx={{ mb: 4 }}>
            <CheckIcon color="success" sx={{ fontSize: 48 }} />
          </Box>

          {destination.type === "CUSTOM_PAGE" ? (
            <Box>
              <Typography variant="h5" gutterBottom>
                Thank you for your submission!
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                {destination.value ||
                  "Your form has been submitted successfully."}
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="h5" gutterBottom>
                Thank you for your submission!
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                {destination.type === "EXTERNAL_REDIRECT"
                  ? "You will be redirected to an external page."
                  : "You will be redirected to the booking page."}
              </Typography>

              <CircularProgress size={24} sx={{ mt: 3 }} />
            </Box>
          )}

          {submissionResult.routeMatched ? (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 3 }}>
              Your submission matched one of our routing rules.
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 3 }}>
              You are seeing our default response.
            </Typography>
          )}
        </ThankYouCard>
      </Container>
    );
  }

  // Render the form
  return (
    <Container>
      <FormContainer component="form" onSubmit={handleSubmit}>
        <FormHeader>
          <Typography variant="h4" component="h1" gutterBottom>
            {formData.name || "Form"}
          </Typography>

          {formData.description && (
            <Typography variant="body1" color="textSecondary">
              {formData.description}
            </Typography>
          )}
        </FormHeader>

        <Divider sx={{ mb: 4 }} />

        {fields.map((field) => (
          <FieldContainer key={field.identifier || field._id}>
            <FieldLabel variant="subtitle1">
              {field.label}
              {field.required && (
                <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                  *
                </Box>
              )}
            </FieldLabel>

            {field.description && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                {field.description}
              </Typography>
            )}

            {/* Render different field types */}
            {field.type === "text" ||
            field.type === "SHORT_TEXT" ||
            field.type === "email" ||
            field.type === "EMAIL" ||
            field.type === "phone" ||
            field.type === "PHONE" ||
            field.type === "url" ||
            field.type === "URL" ? (
              <TextField
                fullWidth
                type={
                  field.type === "email" || field.type === "EMAIL"
                    ? "email"
                    : "text"
                }
                value={formValues[field.identifier] || ""}
                onChange={(e) =>
                  handleFieldChange(field.identifier, e.target.value)
                }
                placeholder={field.placeholder || ""}
                error={!!errors[field.identifier]}
                helperText={errors[field.identifier]}
                required={field.required}
                size="medium"
              />
            ) : field.type === "textarea" || field.type === "LONG_TEXT" ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                value={formValues[field.identifier] || ""}
                onChange={(e) =>
                  handleFieldChange(field.identifier, e.target.value)
                }
                placeholder={field.placeholder || ""}
                error={!!errors[field.identifier]}
                helperText={errors[field.identifier]}
                required={field.required}
                size="medium"
              />
            ) : field.type === "select" || field.type === "SELECT" ? (
              <FormControl fullWidth error={!!errors[field.identifier]}>
                <InputLabel>
                  {field.placeholder || "Select an option"}
                </InputLabel>
                <Select
                  value={formValues[field.identifier] || ""}
                  onChange={(e) =>
                    handleFieldChange(field.identifier, e.target.value)
                  }
                  label={field.placeholder || "Select an option"}
                >
                  {(field.options || []).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors[field.identifier] && (
                  <FormHelperText>{errors[field.identifier]}</FormHelperText>
                )}
              </FormControl>
            ) : field.type === "multi_select" ||
              field.type === "MULTI_SELECT" ? (
              <FormControl fullWidth error={!!errors[field.identifier]}>
                <InputLabel>{field.placeholder || "Select options"}</InputLabel>
                <Select
                  multiple
                  value={formValues[field.identifier] || []}
                  onChange={(e) =>
                    handleFieldChange(field.identifier, e.target.value)
                  }
                  label={field.placeholder || "Select options"}
                >
                  {(field.options || []).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors[field.identifier] && (
                  <FormHelperText>{errors[field.identifier]}</FormHelperText>
                )}
              </FormControl>
            ) : field.type === "checkbox" || field.type === "CHECKBOX" ? (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formValues[field.identifier]}
                      onChange={(e) =>
                        handleFieldChange(field.identifier, e.target.checked)
                      }
                    />
                  }
                  label={field.placeholder || field.label}
                />
                {errors[field.identifier] && (
                  <FormHelperText error>
                    {errors[field.identifier]}
                  </FormHelperText>
                )}
              </Box>
            ) : field.type === "radio" || field.type === "RADIO" ? (
              <FormControl
                component="fieldset"
                error={!!errors[field.identifier]}
              >
                <RadioGroup
                  value={formValues[field.identifier] || ""}
                  onChange={(e) =>
                    handleFieldChange(field.identifier, e.target.value)
                  }
                >
                  {(field.options || []).map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                {errors[field.identifier] && (
                  <FormHelperText>{errors[field.identifier]}</FormHelperText>
                )}
              </FormControl>
            ) : field.type === "number" || field.type === "NUMBER" ? (
              <TextField
                type="number"
                fullWidth
                value={formValues[field.identifier] || ""}
                onChange={(e) =>
                  handleFieldChange(
                    field.identifier,
                    e.target.value === "" ? null : Number(e.target.value)
                  )
                }
                placeholder={field.placeholder || ""}
                error={!!errors[field.identifier]}
                helperText={errors[field.identifier]}
                required={field.required}
                size="medium"
              />
            ) : field.type === "date" || field.type === "DATE" ? (
              <TextField
                type="date"
                fullWidth
                value={formValues[field.identifier] || ""}
                onChange={(e) =>
                  handleFieldChange(field.identifier, e.target.value)
                }
                error={!!errors[field.identifier]}
                helperText={errors[field.identifier]}
                required={field.required}
                size="medium"
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <Typography color="error">
                Unknown field type: {field.type}
              </Typography>
            )}
          </FieldContainer>
        ))}

        <SubmitButton
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          disabled={isSubmitting || processingForm}
          startIcon={
            isSubmitting ? <CircularProgress size={24} /> : <ArrowForwardIcon />
          }
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </SubmitButton>
      </FormContainer>

      {/* Notification for form errors */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default RoutingFormSubmit;
